/**
 * AutoCreatePropertyFromClient renderer.
 * When same_as_appraisal_address == "yes", silently POSTs a new Property
 * with the client's address, links it back, and offers a "Preview client
 * details" link before Next.
 * Skips entirely if trigger_when doesn't match.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2, Eye } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function evaluateCondition(cond, data) {
  if (!cond) return true;
  const m = cond.match(/^\s*([a-z0-9_.]+)\s*==\s*['"]?([^'"]+)['"]?\s*$/i);
  if (!m) return true;
  const [, key, expected] = m;
  const parts = key.split('.'); let v = data || {};
  for (const p of parts) v = v?.[p];
  return String(v) === String(expected);
}

function substitute(t, data) {
  return (t || '').replace(/\{([^}]+)\}/g, (_, k) => {
    const parts = k.split('.'); let v = data || {};
    for (const p of parts) v = v?.[p];
    return v != null ? String(v) : '';
  });
}

export default function AutoCreatePropertyFromClient({ action, capturedData, value, onChange, onNext, onBack, canBack }) {
  const params = action.params || {};
  const shouldRun = evaluateCondition(params.trigger_when, capturedData);
  const [status, setStatus] = useState('idle');   // idle | creating | done | error | skipped
  const [propertyId, setPropertyId] = useState(value?.property_id || null);
  const [error, setError] = useState('');
  const clientId = capturedData?.client_id;
  const clientForm = capturedData?.client_intake_form || {};
  const previewRoute = substitute(params.preview_route || '/clients/{client_id}', capturedData);

  useEffect(() => {
    // Note: Runner shell now skips trigger_when-failing actions during
    // advance/back; this guard only fires if the pointer somehow lands on
    // us directly. We render a placeholder instead of auto-advancing to
    // avoid the "back-button flash and bounce forward" trap.
    if (!shouldRun) { setStatus('skipped'); return; }
    if (status !== 'idle' || propertyId) return;
    (async () => {
      setStatus('creating');
      try {
        const body = {
          client_id: clientId,
          address_num: clientForm.address_num,
          address_street: clientForm.address_street,
          address_suburb: clientForm.address_suburb,
          address_city: clientForm.address_city,
          address_postcode: clientForm.address_postcode,
          notes: 'Auto-created from client intake — same address as client',
        };
        const res = await api.post('/properties', body);
        const newId = res.data?.id || res.data?._id;
        setPropertyId(newId);
        onChange(newId);
        setStatus('done');
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to auto-create property');
        setStatus('error');
      }
    })();
  }, [shouldRun, status, clientId]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'skipped') {
    return (
      <div className="text-sm text-slate-500 italic p-4 border border-dashed border-slate-200 rounded">
        This step is skipped for the current branch. Use <b>Next</b> or <b>Back</b> to continue.
        <div className="mt-3 flex gap-2">
          {canBack && <button onClick={onBack} className="px-3 py-1 text-slate-500 hover:text-slate-700">← Back</button>}
          <span className="flex-1" />
          <button onClick={onNext} className="px-4 py-1 bg-sky-600 text-white rounded">Next →</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold mb-2">⚡ Auto-create Property</div>
        <div className="text-base text-slate-700 mb-2">
          Since the client's address IS the property to appraise, we're creating a Property record automatically.
        </div>
        {status === 'creating' && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Creating property…</div>}
        {status === 'done' && (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2">
            <CheckCircle2 size={14} /> Property created · id <code className="text-xs">{propertyId?.slice(0, 8)}…</code>
          </div>
        )}
        {status === 'error' && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{error}</div>}
      </div>

      {status === 'done' && (
        <a href={previewRoute} target="_blank" rel="noopener noreferrer"
          data-testid="wf-auto-preview-client"
          className="inline-flex items-center gap-1 text-sm text-sky-600 hover:text-sky-800 mb-6">
          <Eye size={14} /> Preview client detail page in a new tab
        </a>
      )}

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={onNext} disabled={status !== 'done'}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">Next →</button>
      </div>
    </div>
  );
}
